import React, { useRef } from "react";
// Components
import { ImageTextListBanner } from "../../components/ImageTextListBanner/ImageTextListBanner";
import { ThreePointSalesBanner } from "../../components/ThreePointSalesBanner/ThreePointSalesBanner";
import {
  PrimaryButton,
  GiftPageFooterBar,
  useOnScreen
} from "@obby/components";
import AuthenticationState from "~/state/AuthenticationState";
import { ReviewsSlider } from "@obby/components";
import { Box, FooterBar } from "./GiftCardPage.layout";
import { Course__CourseUrl } from "@obby/lib";

export function GiftCardPage({
  onSendGiftCard,
  onDiscoverCard,
  reviews,
  courses
}) {
  const headerRef = useRef();

  const isHeaderOnScreen = useOnScreen(headerRef, undefined, {
    rootMargin: "-73px 0px 0px 0px"
  });

  return (
    <Box componentname="gift-card-page" className="gift-card-page">
      <ImageTextListBanner
        discount={AuthenticationState.helpers.getSales()}
        color="cruise"
        title="GIVE THE GIFT OF LEARNING"
        description="With an Obby Gift Card, over 2,500 hand-picked classes, courses and workshops are available for you to get involved in. What will you choose to learn next?"
        size="xLarge"
        image="/static/images/gift-cards/home_image.jpg"
        reverse={true}
        imageFullWidth={true}
        textCss={{ "@lg": { padding: "200px 40px 80px" } }}
      >
        <Box
          ref={headerRef}
          variant="buttonsWrapper"
          componentname="gift-card-page__buttons"
          className="gift-card-page__buttons"
        >
          <Box
            variant="buttonWrapper"
            componentname="gift-card-page__button"
            className="gift-card-page__button"
          >
            <PrimaryButton
              text="Send a gift card"
              uppercase
              color="ebonyClay"
              onClick={onSendGiftCard}
            />
          </Box>
        </Box>
      </ImageTextListBanner>

      <Box variant="containerFluid" css={{ marginY: "2rem" }}>
        <ImageTextListBanner
          border={true}
          color="white"
          title="GIFT ANYTHING, FROM POTTERY TO MEDITATION"
          description="When joining Obby, you get access to our online platform to connect with your teacher on an ongoing basis with access to support and learning materials."
          image="/static/images/gift-cards/home_image_2.jpg"
          imageClassName="smallBannerImage"
          containerMargin={50}
          imgCss={{
            maxHeight: "18.75rem",
            objectFit: "cover",
            borderTopLeftRadius: "$standard",
            borderTopRightRadius: "$standard",

            "@lg": {
              borderBottomLeftRadius: "$standard",
              borderTopRightRadius: "initial",
              maxHeight: "initial",
              height: "100%"
            }
          }}
          textCss={{ padding: "2rem" }}
          points={[
            {
              image: "/static/icons/study-groups-blue.svg",
              title: "Valid on anything",
              text:
                "Gift cards are available on any of our 2,500+ classes - from pottery to meditation to languages & more!"
            },
            {
              image: "/static/icons/study-groups-blue.svg",
              title: "Easy to gift",
              text:
                "Choose to email or print off our e-gift cards or have your Obby gift card delivered by post, to an address of your choice."
            },
            {
              image: "/static/icons/study-groups-blue.svg",
              title: "Obby guarantee",
              text:
                "We ensure quality. If the class or course doesn't live up to your expectations, expect your money back. "
            }
          ]}
        />
      </Box>
      <Box
        variant="containerFluid"
        css={{
          marginBottom: "2rem",
          paddingX: "0",
          "@smd": { paddingX: "1rem" }
        }}
      >
        <ImageTextListBanner
          color="hummingBird"
          title="SUPPORT SMALL BUSINESS"
          description="Send a Gift Card that will make a difference"
          image="/static/images/gift-cards/GiftCardLarge.png"
          imageClassName="giftCardBannerImage"
          reverse={true}
          imgCss={{
            borderRadius: "$standard",
            padding: "1rem",
            "@lg": { padding: "2.5rem", borderRadius: "3rem" }
          }}
          textCss={{ padding: "2rem" }}
          css={{
            "@smd": { flexWrap: "initial", borderRadius: "$standard" },
            "@lg": { paddingY: "2rem" }
          }}
        >
          <Box
            variant="buttonsWrapper"
            componentname="gift-card-page__buttons"
            className="gift-card-page__buttons"
          >
            <Box
              variant="buttonWrapper"
              componentname="gift-card-page__button"
              className="gift-card-page__button"
            >
              <PrimaryButton
                text="discover"
                color="ebonyClay"
                uppercase
                onClick={onDiscoverCard}
              />
            </Box>
          </Box>
        </ImageTextListBanner>
      </Box>

      <Box variant="containerFluid" css={{ "@lg": { paddingBottom: "2rem" } }}>
        <ThreePointSalesBanner
          title="Hand selected classes"
          points={[
            {
              image: "/static/images/quality/trusted.svg",
              title: "Endless opportunities",
              description:
                "With over 2,500 classes, courses, workshops across the UK to choose from, there is no better place to find your perfect class."
            },
            {
              image: "/static/images/quality/opportunities.svg",
              title: "Trusted teachers",
              description:
                "We vet all our teachers personally, to ensure the highest quality teaching - so you don't have to worry"
            },
            {
              image: "/static/images/quality/guarantee.svg",
              title: "Obby guarantee",
              description:
                "We ensure quality. If your class doesn't reach your expectations, we'll give your money back"
            }
          ]}
        />
      </Box>

      <Box
        variant="containerFluid"
        css={{ display: "none", "@smd": { display: "block" } }}
      >
        <Box
          fontSize="medium"
          css={{ textAlign: "center", padding: "1rem 0.5rem" }}
        >
          DON’T JUST TAKE OUR WORD FOR IT
        </Box>
      </Box>
      <Box
        variant="containerFluid"
        css={{
          padding: "2rem 0.5rem 0rem",
          display: "none",
          "@smd": { display: "block" }
        }}
      >
        <ReviewsSlider
          reviews={reviews.map(review => {
            const course = courses.find(
              course => course._id === review.targetId
            );
            return {
              review: review.text,
              rating: review.value,
              reviewer: review.name,
              ...(course
                ? {
                    target: course.title,
                    targetUrl: Course__CourseUrl(course)
                  }
                : {})
            };
          })}
        />
      </Box>

      <Box
        variant="containerFluid"
        css={{ paddingTop: "2rem", "@smd": { paddingBottom: "4rem" } }}
      >
        <ImageTextListBanner
          color="white"
          title="Are you a gift card recipient?"
          description="Apply your Gift Card code during checkout and redeem agaisnt any class, course, workshop or product."
          image="/static/images/workshop-1.png"
          height={500}
          imageClassName="pitch-container__learning-portal-image"
          css={{ borderRadius: "$standard" }}
          textCss={{ padding: "2rem" }}
          imgCss={{
            maxHeight: "18.75rem",
            objectFit: "cover",
            borderTopLeftRadius: "$standard",
            borderTopRightRadius: "$standard",
            "@lg": {
              maxHeight: "initial",
              borderTopRightRadius: "initial",
              borderBottomLeftRadius: "$standard"
            }
          }}
          bulletPointTitleCss={{
            "@sm": {
              "&::after": {
                content: " ",
                backgroundColor: "$robbinsEggBlue",
                height: "0.35rem",
                width: "33%",
                borderRadius: "20px",
                position: "absolute",
                bottom: "-12px",
                left: "0"
              }
            }
          }}
        >
          <Box
            variant="buttonsWrapper"
            componentname="gift-card-page__buttons"
            className="gift-card-page__buttons"
          >
            <Box
              variant="buttonWrapper"
              componentname="gift-card-page__button"
              className="gift-card-page__button"
            >
              <PrimaryButton
                color="ebonyClay"
                uppercase
                text="Redeem your gift"
                onClick={onDiscoverCard}
              />
            </Box>
          </Box>
        </ImageTextListBanner>
      </Box>
      <Box variant="containerFluid" css={{ "@smd": { display: "none" } }}>
        <Box
          fontSize="medium"
          css={{ textAlign: "center", padding: "1rem 0.5rem" }}
        >
          DON’T JUST TAKE OUR WORD FOR IT
        </Box>
      </Box>
      <Box
        variant="containerFluid"
        css={{ paddingBottom: "2rem", "@smd": { display: "none" } }}
      >
        <ReviewsSlider
          mobile
          reviews={reviews.map(review => {
            const course = courses.find(
              course => course._id === review.targetId
            );
            return {
              review: review.text,
              rating: review.value,
              reviewer: review.name,
              ...(course
                ? {
                    target: course.title,
                    targetUrl: Course__CourseUrl(course)
                  }
                : {})
            };
          })}
        />
      </Box>
      <FooterBar show={!isHeaderOnScreen}>
        <GiftPageFooterBar
          rating={10}
          numberOfReviews={6169}
          menuActions={[
            {
              text: "Buy a giftcard now",
              textMode: "uppercase",
              color: "robbinsEggBlue",
              onClick: onSendGiftCard
            }
          ]}
        />
      </FooterBar>
    </Box>
  );
}
